/* eslint-disable react/prop-types */
import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeftIcon, MapPinIcon } from '@heroicons/react/24/solid';
import { apiUrl, apiUrlB2B, centerLocation, timeout } from '../../utils/constants';
import UFInput from '../Input/UFInput';
import { authHeaders, axiosInstance, classifyPhoneNumber, formatLocalPhoneNumber, getDetailLocationFromLatLong, isEmptyData, logError, sendAnalyticsCustomEvent, trackMetaPixelEvent } from '../../utils/helper';
import { HomeIcon, OfficeIcon, OtherIcon } from '../../utils/svg';
import { cloneElement } from 'react';
import { getSavedList } from '../../store/reducers/location';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Loader from '../Loader';
import { setUser } from '../../store/reducers/user';
import useToast from '../Toast';
import useNavigation from '../../hook/useNavigation';
import { GoogleMap, Marker } from '@react-google-maps/api';
import PopupLocation from '../Location';
import { useMemo } from 'react';
import { BottomSheet } from 'react-spring-bottom-sheet';
import CustomGoogleMap from '../GoogleMaps';

const Address = (props) => {
	const { onClose, data, nextPage } = props;
	const dispatch = useDispatch();
	const navigate = useNavigation();

	const { showToast } = useToast();

	const user = useSelector((store) => store?.user || {});
	const savedAddress = useSelector((store) => store?.location.savedData || []);
	const [isLoading, setIsLoading] = useState(false);
	const [isShowPopupLocation, setIsShowPopupLocation] = useState(false);
	const [isShowBottomSheet, setIsShowBottomSheet] = useState(false);

	const userSchema = Yup.object().shape({
		address: Yup.string().required('Alamat wajib diisi'),
		addressName: Yup.string().required('Nama alamat wajib diisi'),
		firstName: Yup.string().required('Nama kontak wajib diisi'),
		primary_phone: Yup.string()
			.required('Nomor kontak wajib diisi')
			.test('is-mobile-phone', 'Hanya nomor ponsel yang diperbolehkan', (value) => {
				if (!value) return false;
				const result = classifyPhoneNumber(value);
				return result.type === 'mobile';
			}),
		email: Yup.string().email('Format email tidak valid').required('Email wajib diisi'),
	});

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {},
		validationSchema: userSchema,
		onSubmit: async (values) => {
			await handleSave(values);
		},
	});

	const { errors, touched, handleSubmit, values, setValues, submitCount } = formik;

	const typeOptions = [
		{
			id: 'save_address_select_home',
			label: 'Home',
			value: 'home',
			icon: <HomeIcon />,
		},
		{
			id: 'save_address_select_office',
			label: 'Office',
			value: 'office',
			icon: <OfficeIcon />,
		},
		{
			id: 'save_address_select_other',
			label: 'Other',
			value: 'other',
			icon: <OtherIcon />,
		},
	];

	const handleSaveAddress = async (values) => {
		try {
			const body = {
				user_id: user?.customerID,
				address: values?.address,
				name: values?.addressName,
				country: values?.country,
				state: values?.state,
				city: values?.city,
				village: values?.village,
				district: values?.district,
				postalCode: values?.postalCode ?? '',
				addressName: values?.addressName,
				addressDetail: values?.detail ?? '',
				type: values?.type,
				contactName: values?.firstName,
				contactNumber: values?.primary_phone,
				latitude: values?.latitude,
				longitude: values?.longitude,
				email: values?.email,
			};

			if (!isEmptyData(values?.id_customer_address)) {
				body.id_customer_address = values?.id_customer_address;
			}

			trackMetaPixelEvent('homepage_delivery_to', {
				address: values?.address,
				saved_address: savedAddress?.map((address) => {
					return {
						customer_id: user?.customerID,
						saved_address_id: address?.id,
						save_address_fill_name: address?.address_name,
						save_address_fill_detail: address?.address_detail,
						select: address?.type,

						save_address_fill_contact_name: address?.contact_name,
						save_address_fill_contact_phone: address?.contact_number,
					};
				}),
			});

			const formData = new FormData();

			// Dynamically append each key-value pair from the object to formData
			Object.entries(body).forEach(([key, value]) => {
				formData.append(key, value);
			});
			const response = await axiosInstance.post(`address/${isEmptyData(body.id_customer_address) ? 'add' : 'edit'}`, formData, authHeaders());
			if (response.status === 200 && response.data.status === 'success') {
				dispatch(getSavedList());
			}
			return true;
		} catch (error) {
			showToast('error', error?.response?.data?.message, 2000);

      logError(error, {feature: "handleSaveAddress"});
			return false;
		}
	};

	const handleUpdateUser = async (values) => {
		try {
			const body = {
				customerFirstName: values?.firstName,
				customerPhone: values?.primary_phone,
				customerCompanyId: user?.companyID,
				customerEmail: values?.email,
				customerB2BData: {
					permission: user?.permission,
					address: {
						address: values?.address,
						addressName: values?.addressName,
						detail: values.detail,
						latitude: values?.latitude,
						longitude: values?.longitude,
						country: values?.country,
						state: values?.state,
						city: values?.city,
						village: values?.village,
						district: values?.district,
						postalCode: values?.postalCode ?? '',
						primary_phone: values?.primary_phone,
					},
				},
			};

			const headers = user?.is_b2b
				? {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${user?.token}`,
					}
				: {};

			await axiosInstance.put(`customers/${user?.customerID}`, body, { headers, baseURL: user?.is_b2b ? apiUrlB2B : apiUrl });
		} catch (error) {
			console.log(error?.response?.data?.message || 'Error in updating user');

      logError(error, {feature: "handleUpdateUser"});
		}
	};

	const handleSave = async (values) => {
		try {
			setIsLoading(true);
			const payload = {
				user_id: user?.customerID,
				...values,
			};
			sendAnalyticsCustomEvent('homepage_delivery_to', payload);
			const saveResult = await handleSaveAddress(values);
			if (user?.is_b2b) {
				await handleUpdateUser(values);
			}
			if (saveResult) {
				dispatch(
					setUser({
						address: values?.address,
						addressName: values?.addressName,
						type: values?.type,
						detail: values?.detail,
						district: values?.district,
						email: values?.email,
						firstName: values?.firstName,
						latitude: values?.latitude,
						longitude: values?.longitude,
						primary_phone: values?.primary_phone,
						postalCode: values?.postalCode ?? '',
						village: values?.village,
						country: values?.country,
						state: values?.state,
						city: values?.city,
					}),
				);
				if (!isEmptyData(nextPage)) {
					return navigate.push(nextPage);
				}
				onClose();
			}
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			showToast('error', error.message, 1000);

      logError(error, {feature: "handleSave"});
		}
	};

	const onLoadMap = useCallback(() => {
		setTimeout(() => {
			const elementsToHide = document.querySelectorAll('.gmnoprint');
			elementsToHide.forEach((el) => (el.style.display = 'none'));
		}, 1000);
	}, []);

	const mapOptions = {
		disableDefaultUI: true,
		zoomControl: false,
		mapTypeControl: false,
		fullscreenControl: false,
		streetViewControl: false,
		rotateControl: false,
		gestureHandling: 'greedy',
	};

	const handleOnClickMap = async (data) => {
		const lat = data?.latLng?.lat();
		const lng = data?.latLng?.lng();
		const place_id = data?.placeId;
		const formated = await getDetailLocationFromLatLong(lat, lng, place_id);
		setValues((prev) => {
			return {
				...prev,
				address: formated?.address,
				latitude: Number(formated?.lat),
				longitude: Number(formated?.lng),
				country: formated?.country,
				state: formated?.state,
				city: formated?.city,
				village: formated?.village,
				district: formated?.district,
				postalCode: formated?.postalCode ?? '',
				center: { lat: Number(formated?.lat), lng: Number(formated?.lng) },
				zoom: 15,
			};
		});
	};

	const handleDragMap = async (data) => {
		const lat = data?.latLng?.lat();
		const lng = data?.latLng?.lng();
		const formated = await getDetailLocationFromLatLong(lat, lng);
		setValues((prev) => {
			return {
				...prev,
				address: formated?.address,
				latitude: Number(formated?.lat),
				longitude: Number(formated?.lng),
				country: formated?.country,
				state: formated?.state,
				city: formated?.city,
				village: formated?.village,
				district: formated?.district,
				postalCode: formated?.postalCode ?? '',
				center: { lat: Number(formated?.lat), lng: Number(formated?.lng) },
				zoom: 15,
			};
		});
	};

	useEffect(() => {
		if (isEmptyData(data)) return;
		setValues((prev) => {
			return {
				...prev,
				...(data?.id && { id_customer_address: data.id }),
				address: data?.address,
				addressName: data?.address_name,
				detail: data?.address_detail,
				type: data?.type,
				firstName: data?.contact_name,
				primary_phone: data?.contact_number,
				email: data?.email,
				latitude: Number(data?.latitude),
				longitude: Number(data?.longitude),
				country: data?.country,
				state: data?.state,
				city: data?.city,
				village: data?.village,
				district: data?.district,
				postalCode: data?.postal_code ?? '',
				center: { lat: Number(data?.latitude), lng: Number(data?.longitude) },
				zoom: 15,
			};
		});
	}, [data]);

	const fetchDefaultLocation = async () => {
		try {
			const lat = user?.latitude ?? centerLocation?.lat;
			const lng = user?.longitude ?? centerLocation?.lng;

			const formated = await getDetailLocationFromLatLong(lat, lng);
			setValues((prev) => ({
				...prev,
				type: 'home',
				email: user?.email,
				firstName: user?.firstName,
				primary_phone: formatLocalPhoneNumber(user?.primary_phone),
				address: formated?.address,
				latitude: Number(formated?.lat),
				longitude: Number(formated?.lng),
				country: formated?.country,
				state: formated?.state,
				city: formated?.city,
				village: formated?.village,
				district: formated?.district,
				postalCode: formated?.postalCode ?? '',
				center: { lat: Number(formated?.lat), lng: Number(formated?.lng) },
				zoom: 15,
			}));
		} catch (error) {
			console.log('reverse geocode error', error);

      logError(error, {feature: "fetchDefaultLocation"});

		}
	};

	useEffect(() => {
		if (isEmptyData(values?.latitude)) {
			fetchDefaultLocation();
		}
	}, []);

	const handleCloseBottomSheet = (data = null) => {
		setIsShowBottomSheet(false);
		if (!isEmptyData(data)) {
			setValues((prev) => {
				return {
					...prev,
					address: data?.address,
					latitude: Number(data?.latitude),
					longitude: Number(data?.longitude),
					country: data?.country,
					state: data?.state,
					city: data?.city,
					village: data?.village,
					district: data?.district,
					postalCode: data?.postalCode ?? '',
					center: { lat: Number(data?.latitude), lng: Number(data?.longitude) },
					zoom: 15,
				};
			});
		}
	};

	const handleClosePopupLocation = () => {
		setIsShowPopupLocation(false);
	};

	const handleClickNew = async () => {
		setIsShowBottomSheet(true);
	};

	const popupLocationProps = useMemo(() => {
		return {
			isOpen: isShowPopupLocation,
			onClose: handleClosePopupLocation,
		};
	}, [isShowPopupLocation]);

	const googleMapProps = useMemo(() => {
		return {
			onClose: handleCloseBottomSheet,
			isFormAddress: true,
		};
	}, []);

	return (
		<>
			<style>
				{`
					[data-rsbs-header] {
						display: none !important;
					}
				`}
			</style>
			{isLoading && <Loader overlay />}
			<div className={`pointer-events-auto h-full w-full overflow-y-auto relative`}>
				<div className='fixed top-0 left-4 mt-4 cursor-pointer z-5' onClick={onClose}>
					<ArrowLeftIcon className='h-8 w-8 text-gray-900 bg-white rounded-full p-1.5 shadow-custom' />
				</div>
				<GoogleMap
					mapContainerClassName='h-96 w-full'
					clickableIcons={!isLoading}
					center={values?.center}
					zoom={values?.zoom}
					onClick={(e) => {
						handleOnClickMap(e);
					}}
					onLoad={onLoadMap}
					options={mapOptions}>
					{values?.center && (
						<Marker
							position={values?.center}
							draggable={!isLoading}
							onDragEnd={(e) => {
								handleDragMap(e);
							}}
						/>
					)}
				</GoogleMap>
				<div className='p-4'>
					<div className='flex justify-between items-center'>
						<div className='flex flex-col'>
							<span className='text-black37 font-semibold text-sm'>Tambahkan Alamat</span>
							<p className={`text-[var(--pumping-spice)] text-xxs font-medium h-[15px] text-red-500 ${!errors?.address && !touched?.address && 'hidden'}`}>{errors?.address && touched?.address && errors?.address}</p>
						</div>
						<button
							id='save_address_search'
							className={`capitalize border border-solid text-[var(--rose-violet)] py-1.5 px-3 text-center text-xs font-semibold rounded hover:border-[var(--rose-violet)] hover:text-[var(--rose-violet)] transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
								errors?.address && touched?.address ? 'border-red-500' : 'border-[var(--rose-violet)]'
							}`}
							onClick={handleClickNew}
							disabled={isLoading}>
							Cari
						</button>
					</div>

					<div className='flex items-start py-4'>
						<MapPinIcon className='h-auto min-w-5 max-w-5 text-rose-violet mr-2' />
						<div className=''>
							<p className='text-black37 text-sm font-semibold'>{values?.district || values?.village}</p>
							<p className='text-black37 text-xxs'>{values?.address}</p>
						</div>
					</div>

					<div className='h-2 w-screen bg-gray-200 bg-opacity-25 -mx-4'></div>

					<div className='py-2'>
						<UFInput
							id='save_address_fill_name'
							label='Nama Alamat'
							required
							type='text'
							placeholder='e.g. Home, Office'
							value={values.addressName}
							errorMessage={
                (touched.addressName || submitCount > 0)
                  ? errors.addressName
                  : ''
              }
							onChange={(event) => {
								setValues((prev) => {
									return {
										...prev,
										addressName: event.target.value,
									};
								});
							}}
							maxLength={25}
							disabled={isLoading}
						/>
						<UFInput
							id='save_address_fill_detail'
							label='Detail Alamat'
							optional
							type='text'
							placeholder='e.g. Floor, unit number'
							value={values.detail}
							errorMessage={
                (touched.detail || submitCount > 0)
                  ? errors.detail
                  : ''
              }
							onChange={(event) => {
								setValues((prev) => {
									return {
										...prev,
										detail: event.target.value,
									};
								});
							}}
							maxLength={41}
							disabled={isLoading}
						/>

						<div className='grid grid-cols-3 gap-4 justify-between py-4'>
							{typeOptions.map((item, index) => (
								<div
									id={item?.id}
									key={index}
									className={`rounded-md border-[3px] border-solid border-[var(--pin-ball)] bg-white text-[var(--dark-gray)] py-2 px-[14px] flex gap-2.5 justify-center items-center ${
										item.value === values.type && 'text-[var(--rose-violet)] border-[var(--rose-violet)]'
									} ${isLoading && 'pointer-events-none opacity-50'}`}
									onClick={() => {
										setValues((prev) => {
											return {
												...prev,
												type: item.value,
											};
										});
									}}>
									{cloneElement(item.icon, { className: `w-4.5 h-auto ${item.value === values.type ? 'fill-[var(--rose-violet)]' : 'fill-gray-500'}` })}
									<span className='font-semibold text-xs'>{item.label}</span>
								</div>
							))}
						</div>

						<UFInput
							id='save_address_fill_contact_name'
							label='Nama Kontak'
							required
							type='text'
							placeholder='e.g. Jhon doe'
							value={values?.firstName}
							errorMessage={
                (touched.firstName || submitCount > 0)
                  ? errors.firstName
                  : ''
              }
							onChange={(event) => {
								setValues((prev) => {
									return {
										...prev,
										firstName: event.target.value,
									};
								});
							}}
							maxLength={25}
							disabled={isLoading}
						/>
						<UFInput
							id='save_address_fill_contact_email'
							label='Email'
							required
							type='email'
							placeholder='Email'
							value={values?.email}
							errorMessage={
                (touched.email || submitCount > 0)
                  ? errors.email
                  : ''
              }
							onChange={(event) => {
								setValues((prev) => {
									return {
										...prev,
										email: event.target.value,
									};
								});
							}}
							maxLength={50}
							disabled={isLoading}
						/>
						<UFInput
							id='save_address_fill_contact_phone'
							label='Nomor Kontak'
							required
							type='tel'
							placeholder='e.g. 81234567890'
							value={values?.primary_phone}
							errorMessage={
                (touched.primary_phone || submitCount > 0)
                  ? errors.primary_phone
                  : ''
              }
							onChange={(event) => {
								const raw = event.target.value;
								const onlyNumbers = raw.replace(/\D/g, '');
								setValues((prev) => {
									return {
										...prev,
										primary_phone: formatLocalPhoneNumber(onlyNumbers),
									};
								});
							}}
							maxLength={15}
							disabled={isLoading}
						/>
						<button
							id='save_address_submit'
							type='submit'
							className='capitalize border border-solid border-[var(--rose-violet)] bg-[var(--rose-violet)] text-white w-full py-2.5 text-center text-xs font-semibold rounded hover:bg-[var(--rose-violet)]/80 transition-all disabled:cursor-not-allowed disabled:opacity-50'
							onClick={handleSubmit}
							disabled={isLoading}>
							Simpan
						</button>
					</div>
				</div>
			</div>
			<PopupLocation {...popupLocationProps} />
			<BottomSheet key={'form-address-google-map'} open={isShowBottomSheet} onDismiss={handleCloseBottomSheet}>
				<CustomGoogleMap {...googleMapProps} />
			</BottomSheet>
		</>
	);
};

export default Address;
